import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * On mobile/carousel, it selects the variant media in the slideshow.
   * On desktop/grid, it scrolls to the variant media in the grid.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const selectedMediaId = event.detail.resource?.featured_media?.id?.toString();

    this.replaceWith(newMediaGallery);

    if (!selectedMediaId) return;

    requestAnimationFrame(() => {
      const mediaItems = Array.from(newMediaGallery.querySelectorAll('.product-media[data-media-id]'));

      const uniqueMediaIds = [];

      for (const item of mediaItems) {
        const mediaId = item.getAttribute('data-media-id');

        if (mediaId && !uniqueMediaIds.includes(mediaId)) {
          uniqueMediaIds.push(mediaId);
        }
      }

      const selectedIndex = uniqueMediaIds.indexOf(selectedMediaId);

      if (selectedIndex === -1) return;

      // Mobile / carousel path
      newMediaGallery.slideshow?.select(selectedIndex, undefined, { animate: false });

      // Desktop / grid path
      if (window.matchMedia('(min-width: 750px)').matches) {
        const selectedDesktopMedia = newMediaGallery.querySelector(
          `.media-gallery__grid .product-media[data-media-id="${selectedMediaId}"]`
        );

        selectedDesktopMedia
          ?.closest('.product-media-container')
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
      }
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}